import { isControl } from '@Common/Controls/Controls';
import { GridLayout } from '@Common/Controls/GridLayout';
import { Splitter } from '@Common/Controls/Splitter';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import urlHelper from '@Common/Helpers/UrlHelper';
import { Scroll } from '@View/Core/Scroll';

export interface IFlowLayoutParameters {
    title?: string | JQuery;
    image?: string;
    sections: IFlowLayoutSection[];
    className?: any;
}

export interface IFlowLayoutSection {
    title: string | JQuery;
    content: any; // JQuery or Insight control or string
    css?: any;
    height?: string;
    collapsed?: boolean;
    hasSplitter?: boolean;
}

export interface IFlowLayoutSectionClickArg {
    index: number;
    collapsed: boolean;
    section: IFlowLayoutSection;
    height: number;
}

export interface IFlowLayoutSectionResizeArg {
    index: number;
    section: IFlowLayoutSection;
    height: number;
}

export class FlowLayout {
    private readonly root: JQuery;
    private readonly scrollContainer: JQuery;
    private readonly gridLayout: GridLayout;
    private readonly scroll: Scroll;
    private flowLayoutParameters: IFlowLayoutParameters;
    private indexes: number[] = [];
    private hasScroll = true;

    private readonly eventBug = new MulticastDelegateBag();
    public readonly onSectionClick = this.eventBug.create<ICallback<IFlowLayoutSectionClickArg>>();
    public readonly onSectionResize = this.eventBug.create<ICallback<IFlowLayoutSectionResizeArg>>();
    public readonly onResize = this.eventBug.create<ICallback<ISize>>();

    constructor(flowLayoutParameters: IFlowLayoutParameters) {
        this.flowLayoutParameters = flowLayoutParameters;

        this.root = $('<div class="flow-layout"/>');
        this.scrollContainer = $('<div/>');
        this.scrollContainer.appendTo(this.root);

        const gridLayoutRows: any[] = [
            {
                style: { height: 'auto' }
            }
        ];
        const titleContent = $('<h3 class="text-light" />');
        if (flowLayoutParameters.image) {
            titleContent.append($('<img/>').attr('src', urlHelper.getImageUrl(flowLayoutParameters.image)));
        }
        if (flowLayoutParameters.title) {
            titleContent.append(flowLayoutParameters.title);
        }
        const gridLayoutCells: any[] = [];
        if (flowLayoutParameters.image || flowLayoutParameters.title) {
            gridLayoutCells.push({
                rowIndex: 0,
                columnIndex: 0,
                className: 'flow-layout-title',
                content: titleContent
            });
        }
        this.hasScroll = !_(flowLayoutParameters.sections).some(section => !!section.height);

        let lastIndex = 0;
        _(flowLayoutParameters.sections).forEach((section: IFlowLayoutSection, index: number) => {
            this.indexes[index] = lastIndex + 1;

            gridLayoutRows.push({ style: { height: 'auto' } });
            gridLayoutCells.push({
                rowIndex: lastIndex + 1,
                columnIndex: 0,
                className: 'flow-layout-section-title text-no-wrap' + (section.collapsed ? ' flow-layout-section-title-selected' : ''),
                content: section.title,
                isLabel: false,
                onClick: cell => {
                    if (this.hasScroll) {
                        const contentRowIndex = this.indexes[index] + 1;
                        if (gridLayoutRows.length > contentRowIndex) {
                            if (section.content) {
                                gridLayoutRows[contentRowIndex].row.toggle();
                                cell.cell.toggleClass('flow-layout-section-title-selected');
                                if (!cell.cell.hasClass('flow-layout-section-title-selected')) {
                                    section.content.adjust && section.content.adjust();
                                }
                            }
                            if (section.hasSplitter) {
                                gridLayoutRows[contentRowIndex + 1].row.toggle();
                            }
                            this.scroll.refresh();

                            const rowIndex = this.indexes[index] + 1;
                            const rowHeight = gridLayoutRows[rowIndex].row.height();
                            this.onSectionClick({
                                index: index,
                                collapsed: cell.cell.hasClass('flow-layout-section-title-selected'),
                                section: section,
                                height: rowHeight
                            });
                        }
                    }
                }
            });
            gridLayoutRows.push({ style: this.hasScroll ? null : { height: section.height } });
            gridLayoutCells.push({
                content: section.content,
                style: section.css,
                rowIndex: lastIndex + 2,
                className: 'flow-layout-section',
                columnIndex: 0
            });

            if (section.hasSplitter) {
                const splitter = new Splitter({
                    isVertical: false,
                    height: '9px',
                    returnAreaControl: true,
                    resetOffset: true
                });
                splitter.onSplitEnd(offset => {
                    const rowIndex = this.indexes[index] + 1;
                    const rowHeight = gridLayoutRows[rowIndex].row.height() - offset;
                    const cellControl = this.gridLayout.cellContent(rowIndex, 0);
                    if (isControl(cellControl)) {
                        const width = cellControl.getControl().width();
                        cellControl.adjust(width, rowHeight);
                    }
                    this.gridLayout.setRowStyle(rowIndex, { height: rowHeight + 'px' });
                    this.scroll.refresh();

                    this.onSectionResize({
                        index: index,
                        section: section,
                        height: rowHeight
                    });
                });
                gridLayoutRows.push({ style: { height: '5px' } });
                gridLayoutCells.push({
                    content: splitter,
                    rowIndex: lastIndex + 3,
                    columnIndex: 0,
                    className: 'flow-layout-splitter splitter horizontal'
                });
                lastIndex = lastIndex + 3;
            } else {
                lastIndex = lastIndex + 2;
            }
        });

        this.gridLayout = new GridLayout({
            className: 'flow-layout-container no-gap',
            columns: [{}],
            rows: gridLayoutRows,
            cells: gridLayoutCells
        });

        if (this.hasScroll) {
            this.flowLayoutParameters.className && this.root.addClass(this.flowLayoutParameters.className);
            this.gridLayout.getControl().appendTo(this.scrollContainer);

            this.scroll = new Scroll(this.root, {
                scrollX: false
            });
        } else {
            this.flowLayoutParameters.className && this.gridLayout.getControl().addClass(this.flowLayoutParameters.className);
        }

        _(flowLayoutParameters.sections).forEach((section: IFlowLayoutSection, index) => {
            const contentRowIndex = this.indexes[index] + 1;
            if (section.collapsed && gridLayoutRows.length > contentRowIndex) {
                gridLayoutRows[contentRowIndex].row.css('display', 'none');
                section.hasSplitter && gridLayoutRows[contentRowIndex + 1].row.css('display', 'none');
            }
        });
    }

    public getControl(): JQuery {
        return this.hasScroll ? this.root : this.gridLayout.getControl();
    }

    public adjust(width: number, height: number): void {
        this.root.css({
            width: width,
            height: height
        });
        if (this.hasScroll) {
            this.scroll.refresh(() => this.resize());
        } else {
            this.gridLayout.adjust(width, height);
            this.onResize(this.getContentSize());
        }
    }

    private resize(): void {
        this.gridLayout.adjust(this.scrollContainer.width(), null);
        this.onResize(this.getContentSize());
    }

    public updateScroll({ bypassTimeout = false, resize = false } = {}): void {
        this.scroll.refresh(resize ? () => this.resize() : null, bypassTimeout);
    }

    public enabled(value: boolean): any {
        this.gridLayout.enabled(value);

        return value;
    }

    public visible(value?: boolean): boolean {
        return this.gridLayout.visible(value);
    }

    public readonly(value?: boolean): boolean {
        return this.gridLayout.readonly(value);
    }

    public setState(value?: boolean): void {
        return this.gridLayout.setState(value);
    }

    public destroy(): void {
        this.gridLayout.destroy();
        this.eventBug.destroy();
        this.scroll?.destroy();
    }

    public getContentSize(): ISize {
        const section = this.root.find('.flow-layout-section').has(':visible').first();
        const sectionGap = section.length > 0 ? section.outerWidth(true) - section.width() : 0;
        const scrollSize = (this.scroll && this.scroll.getVerticalScrollbarSize()) || 0;
        const width = this.root.width() - scrollSize - sectionGap;
        let height = this.root.height();
        this.root
            .find('table > tbody > tr')
            .find('>td.flow-layout-section-title, >td.flow-layout-section, >td.flow-layout-splitter')
            .each((i, e) => {
                const element = $(e);
                if (!element.hasClass('flow-layout-section')) {
                    height -= element.height();
                }
                if (element.is(':visible')) {
                    height -= element.outerHeight(true) - element.height();
                }
            });
        return { height, width };
    }

    public setSectionContent(index: number, content): void {
        this.flowLayoutParameters.sections[index].content = content;
        this.gridLayout.cellContent(this.indexes[index] + 1, 0, content);
    }

    public setSectionProperty(index: number, section: IFlowLayoutSection): void {
        if (section.content) {
            this.setSectionContent(index, section.content);
        }
        if (section.title != undefined) {
            this.gridLayout.cellContent(this.indexes[index], 0, section.title);
        }
    }

    public expandSection(index: number) {
        const titleRowIndex = this.indexes[index];
        const cell = this.gridLayout.getCell(titleRowIndex, 0);
        if (cell.cell.hasClass('flow-layout-section-title-selected')) {
            cell.onClick(cell);
        }
    }
}
